/**
 * Assembly Queue - 2D Assembly Department View
 *
 * 4-Bucket Structure:
 * 1. Waiting on Prints — Upstream blocked, prints not ready
 * 2. Ready to Work — Prints done, can start assembly now
 * 3. In Progress — Active time ticket on assembly operation
 * 4. Finished & Packed Out — POP/PACKOUT complete (visible for 24h)
 *
 * Design follows PA Queue patterns:
 * - KanbanOverview at top showing workflow
 * - Two-column layout (60% queue list, 40% right panel)
 * - Collapsible sections with colored borders
 * - Active worker display for IN_PROGRESS
 * - Rework badges
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useReworkQueue } from "../../hooks/useReworkQueue";
import ReworkQueueItem from "../ReworkQueueItem";
import ReworkWarningBadge from "../ReworkWarningBadge";
import { ProgressDots, STAGE_LABELS } from "../shared/ProgressDots";

const API_BASE = import.meta.env.VITE_API_URL || "";

// Assembly Bucket configuration
const ASSEMBLY_BUCKETS = {
  waiting_on_prints: { label: "Waiting on Prints", color: "amber", order: 0 },
  ready_to_work: { label: "Ready to Work", color: "green", order: 1 },
  in_progress: { label: "In Progress", color: "blue", order: 2 },
  finished: { label: "Finished & Packed Out", color: "purple", order: 3, collapsed: true },
};

// Full Tailwind classes for bucket badges (no interpolation)
const BUCKET_BADGE_CLASSES = {
  in_progress: 'bg-blue-100 text-blue-700',
  ready_to_work: 'bg-green-100 text-green-700',
  waiting_on_prints: 'bg-amber-100 text-amber-700',
  finished: 'bg-purple-100 text-purple-700',
};

// Bucket badge labels (short form)
const BUCKET_BADGE_LABELS = {
  in_progress: 'ACTIVE',
  ready_to_work: 'ASSY',
  waiting_on_prints: 'WAIT',
  finished: 'DONE',
};

export default function AssemblyQueue({ employeeId, displayName, token, onSelectProject }) {
  // Bucket state
  const [buckets, setBuckets] = useState({
    waiting_on_prints: [],
    ready_to_work: [],
    in_progress: [],
    finished: [],
  });
  const [workloadSnapshot, setWorkloadSnapshot] = useState({ today: 0, tomorrow: 0, thisWeek: 0 });
  const [activeWorkers, setActiveWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null); // Selection mode (not expansion)

  // Rework queue - items that need rework at Assembly
  const {
    queueItems: reworkItems,
    warnings: reworkWarnings,
    loading: reworkLoading,
    scrapCount,
    totalCount: reworkCount,
    refetch: refetchRework,
    getWarningsForJob,
    hasRework,
  } = useReworkQueue('ASSEMBLY', { refreshInterval: 60000 });

  // Lazy loading for jobs - only fetch when expanding a project
  const [loadingJobsFor, setLoadingJobsFor] = useState(null);
  const [loadedJobs, setLoadedJobs] = useState({});

  // Data freshness
  const [lastSync, setLastSync] = useState(null);
  const [dataSource, setDataSource] = useState(null);

  // Fetch Assembly Queue data
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE}/api/assembly-queue/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Assembly queue: ${response.status}`);
      }

      const data = await response.json();

      setBuckets(data.buckets || {
        waiting_on_prints: [],
        ready_to_work: [],
        in_progress: [],
        finished: [],
      });
      setWorkloadSnapshot(data.workloadSnapshot || { today: 0, tomorrow: 0, thisWeek: 0 });
      setActiveWorkers(data.activeWorkers || []);

      // Store data freshness info
      setLastSync(data.lastSync ? new Date(data.lastSync) : null);
      setDataSource(data.source || 'unknown');

      console.log(`Assembly Queue: Loaded from ${data.source}, last sync: ${data.lastSync}`);
    } catch (err) {
      console.error("Error fetching Assembly queue:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Lazy load jobs for a project (uses the unified production API)
  const fetchJobsForProject = useCallback(async (quoteNumber) => {
    if (!quoteNumber || loadedJobs[quoteNumber]) return;

    setLoadingJobsFor(quoteNumber);
    try {
      const response = await fetch(`${API_BASE}/api/v2/production/projects/${encodeURIComponent(quoteNumber)}/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLoadedJobs(prev => ({ ...prev, [quoteNumber]: data.jobs || [] }));
      }
    } catch (err) {
      console.error(`Error fetching jobs for ${quoteNumber}:`, err);
    } finally {
      setLoadingJobsFor(null);
    }
  }, [token, loadedJobs]);

  // Selection handler - for right panel display
  const handleSelectProjectForPanel = (project) => {
    const isSameProject = selectedProject?.id === project.id;
    if (isSameProject) {
      setSelectedProject(null);
    } else {
      setSelectedProject(project);
      // Lazy load jobs when selecting (if not already loaded)
      if (project.quoteNumber && !loadedJobs[project.quoteNumber]) {
        fetchJobsForProject(project.quoteNumber);
      }
    }
  };

  // Compute total count
  const totalCount = useMemo(() => {
    return Object.values(buckets).reduce((sum, items) => sum + items.length, 0);
  }, [buckets]);

  if (loading) {
    return (
      <div className="flex-1 bg-slate-200 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading Assembly Queue...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 bg-slate-200 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error: {error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-200 text-gray-900 overflow-auto">
      {/* Header - FULL WIDTH */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              2D Assembly
              {reworkCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">
                  {reworkCount} rework
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-500">
              {totalCount} projects
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Data freshness indicator */}
            {lastSync && (
              <span className="text-xs text-gray-400" title={`Data source: ${dataSource}`}>
                Synced {lastSync.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* REWORK REQUIRED - Priority section at top (ABOVE Kanban) */}
      {reworkCount > 0 && (
        <div className="px-4 pb-4">
          <section className="bg-white rounded-lg border border-red-200 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-3">
              <span className="text-red-500">🔧</span>
              <span className="text-red-700">Rework Required</span>
              <span className="ml-auto px-2 py-0.5 bg-red-100 rounded-full text-xs font-medium text-red-700">
                {reworkCount}
              </span>
              {scrapCount > 0 && (
                <span className="px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">
                  {scrapCount} SCRAP
                </span>
              )}
            </div>
            <div className="space-y-2">
              {reworkItems.map((item) => (
                <ReworkQueueItem
                  key={item.id}
                  item={item}
                  onStart={() => console.log('Start rework:', item)}
                  onView={() => console.log('View rework:', item)}
                />
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Kanban Overview - FULL WIDTH */}
      <div className="px-4 pb-4">
        <KanbanOverview buckets={buckets} onSelectProject={handleSelectProjectForPanel} />
      </div>

      {/* Sections - constrained width */}
      <div className="max-w-7xl mx-auto px-4 pb-4">
        {/* Two-column layout: Queue list (60%) + Right panel (40%) */}
        <div className="flex gap-4">
          {/* Main Queue List - 60% width */}
          <div className="w-[60%] space-y-4">

            {/* Waiting on Prints */}
            <Section
              title={ASSEMBLY_BUCKETS.waiting_on_prints.label}
              count={buckets.waiting_on_prints?.length || 0}
              color={ASSEMBLY_BUCKETS.waiting_on_prints.color}
            >
              {(buckets.waiting_on_prints?.length || 0) === 0 ? (
                <EmptyRow>No jobs waiting on prints</EmptyRow>
              ) : (
                <ProjectList
                  projects={buckets.waiting_on_prints}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                  showStage
                />
              )}
            </Section>

            {/* Ready to Work */}
            <Section
              title={ASSEMBLY_BUCKETS.ready_to_work.label}
              count={buckets.ready_to_work?.length || 0}
              color={ASSEMBLY_BUCKETS.ready_to_work.color}
            >
              {(buckets.ready_to_work?.length || 0) === 0 ? (
                <EmptyRow>No jobs ready - prints in progress</EmptyRow>
              ) : (
                <ProjectList
                  projects={buckets.ready_to_work}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                />
              )}
            </Section>

            {/* In Progress */}
            <Section
              title={ASSEMBLY_BUCKETS.in_progress.label}
              count={buckets.in_progress?.length || 0}
              color={ASSEMBLY_BUCKETS.in_progress.color}
            >
              {(buckets.in_progress?.length || 0) === 0 ? (
                <EmptyRow>No active assembly work</EmptyRow>
              ) : (
                <ProjectList
                  projects={buckets.in_progress}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                  showWorkers
                />
              )}
            </Section>

            {/* Finished & Packed Out (collapsed by default) */}
            {(buckets.finished?.length || 0) > 0 && (
              <Section
                title={ASSEMBLY_BUCKETS.finished.label}
                count={buckets.finished.length}
                color={ASSEMBLY_BUCKETS.finished.color}
                collapsed
              >
                <ProjectList
                  projects={buckets.finished}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                  showCompletedAt
                />
              </Section>
            )}
          </div>

          {/* Right Panel - 40% width */}
          <div className="w-[40%] flex-shrink-0">
            <div className="sticky top-24 space-y-4">
              {/* SKU Detail Panel */}
              <SKUDetailPanel
                project={selectedProject}
                jobs={loadedJobs[selectedProject?.quoteNumber] || []}
                loading={loadingJobsFor === selectedProject?.quoteNumber}
                onViewDetail={onSelectProject}
                onClose={() => setSelectedProject(null)}
              />

              {/* Workload Snapshot - smaller when project selected */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Workload Snapshot</h3>
                <div className="space-y-2">
                  <WorkloadRow label="Today" count={workloadSnapshot.today} urgent />
                  <WorkloadRow label="Tomorrow" count={workloadSnapshot.tomorrow} />
                  <WorkloadRow label="This Week" count={workloadSnapshot.thisWeek} />
                </div>
              </div>

              {/* Active Workers */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Active Workers
                  {activeWorkers.length > 0 && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      ({activeWorkers.length})
                    </span>
                  )}
                </h3>
                {activeWorkers.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No one working on assembly</p>
                ) : (
                  <div className="space-y-2">
                    {activeWorkers.map((worker, idx) => (
                      <ActiveWorkerRow key={idx} worker={worker} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   HELPER FUNCTIONS
───────────────────────────────────────────────────────────────────────────── */

function formatShipDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "Overdue";
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays <= 7) {
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return date < now;
}

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────────────────────────────────────────── */

function KanbanOverview({ buckets, onSelectProject }) {
  const bucketOrder = ['waiting_on_prints', 'ready_to_work', 'in_progress', 'finished'];

  return (
    <div className="mb-4 bg-white rounded-lg border border-gray-200 p-4">
      {/* Workflow label */}
      <div className="flex items-center gap-2 mb-3 text-xs text-gray-400">
        <span>Assembly Workflow</span>
        <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent" />
      </div>

      <div className="flex items-stretch">
        {bucketOrder.map((bucketKey, index) => {
          const config = ASSEMBLY_BUCKETS[bucketKey];
          const projects = buckets[bucketKey] || [];
          const isLast = index === bucketOrder.length - 1;

          return (
            <div key={bucketKey} className="flex items-stretch flex-1 min-w-0">
              <KanbanColumn
                title={config.label}
                color={config.color}
                projects={projects}
                onSelectProject={onSelectProject}
                stepNumber={index + 1}
              />
              {/* Arrow between columns */}
              {!isLast && (
                <div className="flex items-center px-1 text-gray-300">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KanbanColumn({ title, color, projects, onSelectProject, stepNumber }) {
  const colorClasses = {
    amber: { header: 'bg-amber-100 text-amber-800 border-amber-200', body: 'bg-amber-50/30', accent: 'bg-amber-500' },
    green: { header: 'bg-green-100 text-green-800 border-green-200', body: 'bg-green-50/30', accent: 'bg-green-500' },
    blue: { header: 'bg-blue-100 text-blue-800 border-blue-200', body: 'bg-blue-50/30', accent: 'bg-blue-500' },
    purple: { header: 'bg-purple-100 text-purple-800 border-purple-200', body: 'bg-purple-50/30', accent: 'bg-purple-500' },
  };

  const colors = colorClasses[color] || colorClasses.green;

  // Calculate bucket totals
  const totalSKUs = projects.reduce((sum, p) => sum + (p.jobCount || 0), 0);
  const totalQty = projects.reduce((sum, p) => sum + (p.totalQty || 0), 0);

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      {/* Column header */}
      <div className={`px-3 py-2 rounded-t-lg border ${colors.header} ${colors.body}`}>
        <div className="flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full ${colors.accent} text-white text-[10px] font-bold flex items-center justify-center`}>
            {stepNumber}
          </span>
          <span className="text-xs font-bold truncate">{title}</span>
        </div>
        <div className="text-xs font-medium mt-1 opacity-70">{projects.length} project{projects.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Column body - show project rows with scrolling */}
      <div className={`flex-1 border border-t-0 border-gray-200 px-1.5 py-1.5 ${colors.body} max-h-[280px] overflow-y-auto`}>
        {/* Column headers for SKUs/Qty */}
        <div className="flex items-center gap-1.5 px-1.5 py-0.5 text-[9px] text-gray-400 font-medium border-b border-gray-200 mb-1">
          <span className="w-10 flex-shrink-0">Quote</span>
          <span className="flex-1 truncate">Project</span>
          <span className="w-6 text-right">SKU</span>
          <span className="w-8 text-right">Qty</span>
        </div>
        {projects.length === 0 ? (
          <div className="text-xs text-gray-400 text-center py-3 italic">No jobs</div>
        ) : (
          <div className="space-y-0.5">
            {projects.map(p => (
              <KanbanRow key={p.id} project={p} onSelect={onSelectProject} />
            ))}
          </div>
        )}
      </div>

      {/* Column footer - totals */}
      <div className={`border border-t-0 border-gray-200 rounded-b-lg px-1.5 py-1.5 ${colors.body}`}>
        <div className="flex items-center gap-1.5 px-1.5 py-0.5 text-[10px] font-semibold border-t border-gray-300 pt-1">
          <span className="flex-1 text-gray-600">Total:</span>
          <span className="w-6 text-right text-blue-700">{totalSKUs}</span>
          <span className="w-8 text-right text-indigo-700">{totalQty.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

function KanbanRow({ project, onSelect }) {
  return (
    <div
      onClick={() => onSelect?.(project)}
      className="flex items-center gap-1.5 px-1.5 py-0.5 rounded cursor-pointer hover:bg-white transition-colors text-[10px]"
    >
      {project.hasRework && (
        <span className="px-1 py-0.5 bg-red-100 text-red-600 text-[8px] rounded font-bold">RW</span>
      )}
      <span className="font-mono text-green-600 font-medium w-10 flex-shrink-0">{project.quoteNumber}</span>
      <span className="text-gray-700 truncate flex-1 font-medium">{project.projectName}</span>
      <span className="text-blue-600 font-semibold w-6 text-right" title="Jobs">{project.jobCount || 0}</span>
      <span className="text-indigo-600 font-semibold w-8 text-right" title="Qty">{project.totalQty?.toLocaleString() || 0}</span>
    </div>
  );
}

function Section({ title, count, color, children, collapsed: defaultCollapsed = true }) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const colorClasses = {
    amber: { dot: "bg-amber-500", border: "border-amber-300", bg: "bg-amber-50/50", header: "bg-amber-100", text: "text-amber-700" },
    green: { dot: "bg-green-500", border: "border-green-300", bg: "bg-green-50/50", header: "bg-green-100", text: "text-green-700" },
    blue: { dot: "bg-blue-500", border: "border-blue-300", bg: "bg-blue-50/50", header: "bg-blue-100", text: "text-blue-700" },
    purple: { dot: "bg-purple-500", border: "border-purple-300", bg: "bg-purple-50/50", header: "bg-purple-100", text: "text-purple-700" },
  };

  const colors = colorClasses[color] || colorClasses.green;

  return (
    <section className="mb-4">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`
          w-full flex items-center gap-2 text-xs font-semibold uppercase tracking-wider
          px-3 py-2 rounded-lg cursor-pointer transition-all
          border-l-4 ${colors.border} ${colors.bg}
          hover:shadow-sm
        `}
      >
        <svg
          className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
        <span className={colors.text}>{title}</span>
        <span className={`ml-auto px-2 py-0.5 ${colors.header} rounded-full text-xs font-medium ${colors.text}`}>
          {count}
        </span>
      </button>
      {!isCollapsed && (
        <div className="mt-2 bg-white rounded-lg border border-gray-200 overflow-hidden">
          {children}
        </div>
      )}
    </section>
  );
}

function ProjectList({
  projects,
  selectedId,
  onSelect,
  onViewDetail,
  showStage,
  showWorkers,
  showCompletedAt,
}) {
  return (
    <div className="divide-y divide-gray-200/50">
      {/* Header row - matches PA Queue format */}
      <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs text-gray-500 font-medium bg-white/80 border-b border-gray-100">
        <div className="col-span-2">Quote ID</div>
        <div className="col-span-4">Project Name</div>
        <div className="col-span-1 text-center"># Jobs</div>
        <div className="col-span-1 text-center">Total Qty</div>
        <div className="col-span-2 text-center">
          {showStage && "Stage"}
          {showWorkers && "Workers"}
          {showCompletedAt && "Done"}
          {!showStage && !showWorkers && !showCompletedAt && "Status"}
        </div>
        <div className="col-span-2 text-right">Ship Date</div>
      </div>
      {/* Project rows */}
      {projects.map((project) => (
        <ProjectRow
          key={project.id}
          project={project}
          isSelected={selectedId === project.id}
          onSelect={() => onSelect(project)}
          showStage={showStage}
          showWorkers={showWorkers}
          showCompletedAt={showCompletedAt}
        />
      ))}
    </div>
  );
}

function ProjectRow({
  project,
  isSelected,
  onSelect,
  showStage,
  showWorkers,
  showCompletedAt,
}) {
  // Determine if this is a split project (same quote appears in multiple buckets)
  const isSplitProject = project.totalJobCount > project.jobCount;
  // Show badge for in_progress (always) OR split projects
  const showBucketBadge = project.bucket === 'in_progress' || isSplitProject;

  return (
    <div
      onClick={onSelect}
      className={`grid grid-cols-12 gap-2 px-4 py-2.5 items-center cursor-pointer transition-colors ${
        isSelected
          ? "bg-green-50 border-l-4 border-green-500"
          : "hover:bg-gray-50 border-l-4 border-transparent"
      }`}
    >
      {/* Quote Number + Rework indicator + Bucket badge */}
      <div className="col-span-2 flex items-center gap-1.5">
        {project.hasRework && (
          <span className="px-1 py-0.5 bg-red-100 text-red-600 text-[8px] rounded font-bold flex-shrink-0" title="Rework">RW</span>
        )}
        <span className="font-mono text-xs text-green-600 font-medium">
          {project.quoteNumber || "—"}
        </span>
        {/* Bucket badge - show for in_progress (always) OR split projects */}
        {showBucketBadge && project.bucket && (
          <span className={`px-1 py-0.5 text-[8px] rounded font-medium ${BUCKET_BADGE_CLASSES[project.bucket] || 'bg-gray-100 text-gray-700'}`}>
            {BUCKET_BADGE_LABELS[project.bucket] || project.bucket}
          </span>
        )}
      </div>

      {/* Project Name */}
      <div className="col-span-4">
        <span className="text-sm text-gray-900 truncate block font-medium">{project.projectName}</span>
        {project.customerName && (
          <span className="text-xs text-gray-500 truncate block">{project.customerName}</span>
        )}
      </div>

      {/* Job Count - show bucket/total for split projects */}
      <div className="col-span-1 text-center">
        <span className="text-sm text-blue-600 font-semibold">
          {project.jobCount || 0}
          {isSplitProject && (
            <span className="text-gray-400 text-xs font-normal">/{project.totalJobCount}</span>
          )}
        </span>
      </div>

      {/* Total Qty - show bucket/total for split projects */}
      <div className="col-span-1 text-center">
        <span className="text-sm text-indigo-600 font-semibold">
          {project.totalQty?.toLocaleString() || 0}
          {isSplitProject && project.totalProjectQty > project.totalQty && (
            <span className="text-gray-400 text-xs font-normal">/{project.totalProjectQty?.toLocaleString()}</span>
          )}
        </span>
      </div>

      {/* Dynamic column: Stage/Workers/Completed */}
      <div className="col-span-2 text-center">
        {showStage && (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">
            {project.stage}
          </span>
        )}
        {showWorkers && project.workerCount > 0 && (
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">
            {project.workerCount === 1
              ? project.activeWorkers?.[0] || "1"
              : `${project.workerCount}`}
          </span>
        )}
        {showCompletedAt && (
          <span className="text-xs text-gray-500">
            {formatTimeAgo(project.completedAt)}
          </span>
        )}
        {!showStage && !showWorkers && !showCompletedAt && (
          <span className="text-xs text-gray-400">—</span>
        )}
      </div>

      {/* Ship Date */}
      <div className="col-span-2 text-right">
        <span className={`text-xs font-medium ${isOverdue(project.shipDate) ? 'text-red-600' : 'text-gray-600'}`}>
          {formatShipDate(project.shipDate)}
        </span>
      </div>
    </div>
  );
}

// SKU Detail Panel - shows selected project's SKU details in right panel
function SKUDetailPanel({
  project,
  jobs,
  loading,
  onViewDetail,
  onClose,
}) {
  if (!project) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 border-dashed p-8 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <p className="text-sm text-gray-600 font-medium mt-3">Select a Project</p>
        <p className="text-xs text-gray-400 mt-1">Click on a project to view SKU details</p>
      </div>
    );
  }

  // Filter jobs by project's jobNumbers (for multi-bucket filtering)
  const relevantJobs = project.jobNumbers?.length
    ? jobs.filter(job => project.jobNumbers.includes(job.jobNumber))
    : jobs;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-green-50 border-b border-green-100">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-green-700 font-bold">{project.quoteNumber}</span>
            {project.hasRework && (
              <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] rounded font-bold">REWORK</span>
            )}
          </div>
          <p className="text-sm text-gray-700 font-medium truncate mt-0.5">{project.projectName}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-green-100 rounded text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Project info */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div>
            <span className="text-gray-500">Customer:</span>
            <span className="ml-1 text-gray-900">{project.customerName || '—'}</span>
          </div>
          <div>
            <span className="text-gray-500">PM:</span>
            <span className="ml-1 text-gray-900">{project.pm || '—'}</span>
          </div>
          <div>
            <span className="text-gray-500">Ship Date:</span>
            <span className={`ml-1 font-medium ${isOverdue(project.shipDate) ? 'text-red-600' : 'text-gray-900'}`}>
              {formatShipDate(project.shipDate) || '—'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Jobs:</span>
            <span className="ml-1 text-gray-900">{project.jobCount || 0}</span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-4 py-2 border-b border-gray-100 flex gap-2">
        {onViewDetail && (
          <button
            onClick={() => onViewDetail(project)}
            className="flex-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium flex items-center justify-center gap-1"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Full Details
          </button>
        )}
      </div>

      {/* SKU List */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500" />
            <span className="ml-2 text-sm text-gray-500">Loading SKUs...</span>
          </div>
        ) : relevantJobs.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">
            No SKU details available
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {relevantJobs.map((job, idx) => (
              <SKURow key={job.id || job.jobNumber || idx} job={job} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// SKU Row component for detail panel
function SKURow({ job }) {
  // Map skuClass to readable product type
  const getProductType = (skuClass) => {
    const types = {
      '2D': '2D Print',
      '3D': '3D Print',
      'COMBO': 'Combo',
      'SAMPLE': 'Sample',
      'KIT': 'Kit',
    };
    return types[skuClass] || skuClass || null;
  };

  const productType = getProductType(job.skuClass);

  return (
    <div className="px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-green-600 font-medium">{job.jobNumber}</span>
            {job.sku && job.sku !== job.jobNumber && (
              <span className="text-[10px] text-gray-400">({job.sku})</span>
            )}
            {productType && (
              <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-medium">
                {productType}
              </span>
            )}
          </div>
          {job.description && (
            <p className="text-sm text-gray-800 truncate mt-0.5">{job.description}</p>
          )}
          {/* Job Progress - Per-SKU canonical stage from Job Progress Contract */}
          {job.stage ? (
            <div className="flex items-center gap-1.5 mt-1">
              <ProgressDots stage={job.stage} size="sm" />
              <span className="text-[10px] text-gray-500">{STAGE_LABELS[job.stage] || job.stage}</span>
            </div>
          ) : job.status && (
            <div className="mt-1">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                job.status === 'Complete' ? 'bg-green-100 text-green-700' :
                job.status === 'Active' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {job.status}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-sm font-semibold text-indigo-600">{job.qty?.toLocaleString() || 0}</span>
        </div>
      </div>
    </div>
  );
}

function WorkloadRow({ label, count, urgent }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${urgent ? 'text-amber-600 font-medium' : 'text-gray-600'}`}>{label}</span>
      <span className={`text-lg font-bold ${urgent ? 'text-amber-600' : 'text-gray-900'}`}>{count}</span>
    </div>
  );
}

function ActiveWorkerRow({ worker }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
      <div>
        <span className="text-sm text-gray-900 font-medium">{worker.employeeName}</span>
        <span className="text-xs text-gray-500 ml-2">Q{worker.quoteNumber}</span>
      </div>
      <span className="text-xs text-gray-400">{formatTimeAgo(worker.startedAt)}</span>
    </div>
  );
}

function EmptyRow({ children }) {
  return <div className="px-4 py-6 text-center text-gray-500 text-sm">{children}</div>;
}
